import { Card, Tag, Flex } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faLayerGroup,
  faEdit,
  faEye,
  faTrash,
  faDumbbell,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { Programmazione } from "../types/Programmazione";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/auth";
import { Roles } from "../enums/Roles";
import { useClipboard } from "../contexts/clipboard";
import { useToasts } from "../contexts/toasts";
import dayjs from "dayjs";
import "dayjs/locale/it";
import "./ProgrammazioneCard.css";
import "./list-items/ExerciseItem.css";

dayjs.locale("it");

type Props = {
  programmazione: Programmazione;
  onDelete?: (programmazione: Programmazione) => void;
};

export default function ProgrammazioneCard({ programmazione, onDelete }: Props) {
  const navigate = useNavigate();
  const { loggedUser } = useAuth();
  const { copySplits } = useClipboard();
  const { showToast } = useToasts();

  const canEdit =
    loggedUser?.role === Roles.Superadmin ||
    (loggedUser?.role === Roles.Trainer && programmazione.userId === loggedUser?.id);

  const handleCardClick = useCallback(() => {
    navigate(`/programmazioni/${programmazione.id}`);
  }, [navigate, programmazione.id]);

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigate(`/programmazioni/${programmazione.id}/edit`);
    },
    [navigate, programmazione.id]
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.(programmazione);
    },
    [onDelete, programmazione]
  );

  const handleCopyClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (programmazione.splits?.length) {
        copySplits(programmazione.splits);
        showToast("copy-programmazione", "Programmazione copiata", "", "success");
      }
    },
    [programmazione.splits, copySplits, showToast]
  );

  const scadenzaFormatted = programmazione.scadenza
    ? dayjs(programmazione.scadenza).format("DD/MM/YYYY")
    : "Nessuna scadenza";

  const totalExercises = programmazione.splits?.reduce(
    (acc, split) => acc + (split.exercises?.length || 0),
    0
  );

  return (
    <Card
      hoverable
      onClick={handleCardClick}
      className="programmazione-card"
    >
      <Flex vertical gap="middle" style={{ height: "100%" }}>
        {/* Titolo centrato */}
        <Flex
          align="center"
          justify="center"
          style={{
            flex: 1,
            minHeight: "80px",
          }}
        >
          <h5
            className="mb-0 text-center"
            style={{
              fontWeight: 600,
              color: "var(--app-ink)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
            }}
          >
            {programmazione.name}
          </h5>
        </Flex>

        {/* Badge info */}
        <Flex justify="center" gap="small" wrap="wrap">
          <Tag color="success" style={{ fontSize: "11px", margin: 0, borderRadius: "12px" }}>
            <FontAwesomeIcon icon={faLayerGroup} className="me-1" />
            {programmazione.nSplits} Split{programmazione.nSplits !== 1 ? "s" : ""}
          </Tag>
          {totalExercises !== undefined && totalExercises > 0 && (
            <Tag color="blue" style={{ fontSize: "11px", margin: 0, borderRadius: "12px" }}>
              <FontAwesomeIcon icon={faDumbbell} className="me-1" />
              {totalExercises} Es.
            </Tag>
          )}
          <Tag 
            color={programmazione.scadenza ? "orange" : "default"} 
            style={{ fontSize: "11px", margin: 0, borderRadius: "12px" }}
          >
            <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
            {programmazione.scadenza
              ? dayjs(programmazione.scadenza).format("DD/MM/YY")
              : "Nessuna"}
          </Tag>
          {programmazione.isPublic && (
            <Tag color="purple" style={{ fontSize: "11px", margin: 0, borderRadius: "12px" }}>
              Pubblica
            </Tag>
          )}
        </Flex>

        {/* Pulsanti azioni */}
        <Flex justify="center" gap="small" className="mt-2">
          <button
            className="circle-button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/programmazioni/${programmazione.id}`);
            }}
            title="Visualizza"
          >
            <FontAwesomeIcon size="sm" color="#9c1419" icon={faEye} />
          </button>
          <button
            className="circle-button"
            onClick={handleCopyClick}
            title="Copia Programmazione"
          >
            <FontAwesomeIcon size="sm" color="#9c1419" icon={faCopy} />
          </button>
          {canEdit && (
            <button
              className="circle-button"
              onClick={handleEditClick}
              title="Modifica"
            >
              <FontAwesomeIcon size="sm" color="#9c1419" icon={faEdit} />
            </button>
          )}
          {canEdit && (
            <button
              className="circle-button"
              onClick={handleDeleteClick}
              title="Elimina"
            >
              <FontAwesomeIcon size="sm" color="#dc3545" icon={faTrash} />
            </button>
          )}
        </Flex>
      </Flex>
    </Card>
  );
}
